<template>
  <v-menu v-model="menu" offset-y :close-on-content-click="false">
    <!-- Activator -->
    <template v-slot:activator="{ on, attrs }">
      <v-text-field
        ref="treeSelect"
        v-bind="attrs"
        v-on="on"
        :label="label"
        readonly
        hide-details
        clearable
        append-icon="mdi-chevron-down"
        @click:append="toggleMenu"
        @click="toggleMenu"
        @click:clear="clearSelection"
        :class="{ 'menu-open': menu }"
        class="tree-select-v-select"
        :value="displayValue"
      >
      </v-text-field>
    </template>

    <!-- Dropdown content -->
    <v-card style="max-height: 400px; overflow-y: auto;">
      <v-treeview
        :items="items"
        selectable
        selection-type="leaf"
        open-on-click
        item-key="id"
        item-text="name"
        item-children="children"
        v-model="selected"
        :open.sync="open"
        expand-icon=""
      >
        <!-- Custom right-side arrow for nodes -->
        <template v-slot:label="{ item, open }">
          <div class="d-flex align-center justify-space-between w-100">
            <span>{{ item.name }}</span>
            <v-icon
              v-if="!isLeaf(item)"
              class="node-arrow"
              :class="{ open: open }"
            >
              mdi-chevron-down
            </v-icon>
          </div>
        </template>
      </v-treeview>
    </v-card>
  </v-menu>
</template>

<script>
export default {
  name: "TreeSelect",
  props: {
    value: {
      type: Array,
      default: () => [] // selected IDs
    },
    items: {
      type: Array,
      required: true
    },
    label: {
      type: String,
      default: "Select items"
    }
  },
  data() {
    return {
      menu: false,
      open: [],
      selected: [...this.value] // local copy of v-model
    }
  },
  computed: {
    // Map of all items for convenience
    itemMap() {
      return this.buildMap(this.items)
    },
    displayValue() {
      return this.selectedFlattened.join(", ")
    },
    // Flattened selection with parent logic
    selectedFlattened() {
      const names = []

      const traverse = item => {
        if (!this.isLeaf(item)) {
          if (this.areAllChildrenSelected(item)) {
            names.push(item.name) // show parent if all children selected
          } else {
            item.children.forEach(child => traverse(child)) // recurse
          }
        } else {
          if (this.selected.includes(item.id)) {
            names.push(item.name)
          }
        }
      }

      this.items.forEach(item => traverse(item))
      return names
    }
  },
  watch: {
    selected(newVal) {
      this.$emit("update:treeSelectedItems", newVal) // send selected ids to parent
    },
    value(newVal) {
      this.selected = [...newVal] // keep local copy in sync
    }
  },
  methods: {
    toggleMenu() {
      this.menu = !this.menu

      // Blur the input when closing so label floats down
      if (!this.menu && this.$refs.treeSelect) {
        const inputEl = this.$refs.treeSelect.$el.querySelector("input")
        if (inputEl) {
          inputEl.blur()
        }
      }
    },
    clearSelection() {
      this.selected = [] // clears all selected leaf IDs
      this.open = [] // close the tree
    },
    // To make nodes with an empty childrens array act like a leaf node
    isLeaf(item) {
      return !item.children || item.children.length === 0
    },
    // Checks recursively if all children of a node are selected
    areAllChildrenSelected(item) {
      if (this.isLeaf(item)) {
        return this.selected.includes(item.id)
      }
      return item.children.every(child => this.areAllChildrenSelected(child))
    },

    // Build lookup map of id -> {name, level, parentId}
    buildMap(items, level = 1, parent = null, map = {}) {
      items.forEach(item => {
        map[item.id] = {
          ...item,
          level,
          parentId: parent ? parent.id : null
        }

        if (!this.isLeaf(item)) {
          this.buildMap(item.children, level + 1, item, map)
        }
      })
      return map
    }
  }
}
</script>

<style>
.tree-select-v-select .v-input__icon--append {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  transform-origin: center;
}
.tree-select-v-select.menu-open .v-input__icon--append {
  transform: rotate(180deg);
}
.node-arrow {
  transition: transform 0.3s ease;
  transform-origin: center;
}
.node-arrow.open {
  transform: rotate(180deg);
}
</style>
